import * as React from "react";
import SignatureCanvas from "react-signature-canvas";
import {ColorPicker} from "@bizform/primereact/components/colorpicker/ColorPicker";
import {OverlayPanel} from "@bizform/primereact/components/overlaypanel/OverlayPanel";
import {Spinner} from "@bizform/primereact/components/spinner/Spinner";
import {Button} from "@bizform/primereact/components/button/Button";
import {Static} from "@bizform/type/decorator/Static.decorator";
import {
    BaseStateModel,
    ComponentElement,
    ComponentElementDisplayMode,
    ComponentElementStatic
} from "@bizform/type/model/component_element/ComponentElement.model";
import {
    ComponentCategory,
    ComponentElementManifest,
    ComponentElementProperty
} from "@bizform/type/model/component_element/ComponentElementManifest.model";
import {DataType} from "@bizform/type/model/model/DataType.model";
import {ComponentHelper, Debug, DomHelper, ObjectHelper} from "@bizform/helper/dist/js/bizflow/helper.min";
import {ComponentElementClass, ReactComponentElementInit} from "@bizform/core/dist/js/bizflow/core.min";
import {AbstractComponentElement} from "../AbstractComponentElement";
import {I18n} from "../../misc/model/I18n.model";
import {SignatureHelper} from "./helper/Signature.helper";


type Props = {
};

type WrappedCompState = {
    value?: string;
    tabindex?: number;
    readOnly?: boolean;
    userProps?: {
        lineWidth?: number;
        lineColor?: any;
    };
    showIcon?: boolean;
    canvasStyle?: any;
    signStyle?: { margin?: string, height?: number };
    placeholder?: string;
    isErase?: boolean;
    changed?: boolean;
    autoSaveOnHide?: boolean;
    metaInfo?: string;
};

type WrappedFormState = {
};

type State = BaseStateModel & {
    wrappedCompState: WrappedCompState;
    wrappedFormState: WrappedFormState;
};

/**npm
 * @since 0.0.1
 * @author Hwansoon Park
 * @class BfSignature
 * @description BfSignature component element
 */
@ComponentElementClass()
@Static<ComponentElementStatic<string>>()
export class BfSignature extends AbstractComponentElement<Props, State> {
    static readonly componentElementManifest: ComponentElementManifest = {
        uniqueId: "_D1Z7gG0d8IO3PozFJ9hPng4M1dqudLx",
        category: ComponentCategory.MISC,
        label: I18n.componentElement.signature.label,
        description: I18n.componentElement.signature.description,
        icon: "",
        dataType: [DataType.SIGN],
        property: null
    };

    private _init: boolean;

    focus(): void {
        if (this._button) {
            this._button.focus();
        }
    }

    setProperty(property: ComponentElementProperty): void {
        try {
            const newState = ComponentHelper.createStateWithProperties<State, React.FormEvent<HTMLInputElement>>(property, this);
            newState.wrappedCompState.userProps = {
                lineWidth: newState.wrappedCompState.canvasStyle.defaultLineWidth,
                lineColor: newState.wrappedCompState.canvasStyle.defaultLineColor
            };
            newState.wrappedCompState.canvasStyle.width = parseInt(newState.wrappedCompState.canvasStyle.width) < 230 ? 230 : newState.wrappedCompState.canvasStyle.width;
            if (this._init) {
                newState.wrappedCompState.value = this.state.wrappedCompState.value;
            }
            this._init = true;
            this.setState(newState, this._canvasHide);
        } catch (e) {
            this.emitDidCatch(e);
        }
    }

    emit(value: string): void {
    }

    emitFormState(value: string): void {
    }

    subscription(value: string): void {
        const newState = {
            wrappedCompState: {
                ...this.state.wrappedCompState,
                value: value && value.split ? value.split("@")[0] : value
            }
        };

        this.setState(newState, this._loadCanvas);
    }

    @ReactComponentElementInit(BfSignature)
    static init(): ComponentElement<string> {
        return;
    }

    initDone(): void {
    }

    destroy(): void {
        // proceed something to clean up
        this.destroyDone();
    }

    destroyDone(): void {
    }

    /* for React */
    props: Props;
    state: State;

    private _signature: any;
    private _button: any;
    private _inputLineWidth: any;
    private _colorPicker: any;
    private _overlayPanel: any;
    private _overlayPanelToolBox: HTMLDivElement;

    constructor(props?: Props) {
        super(props);

        this.state = {
            show: false,
            displayMode: ComponentElementDisplayMode.INIT,
            wrappedCompState: {
                // must be assigned value property in the case of InputText
                // To avoid warning it("A component is changing an uncontrolled input of type undefined to be controlled.")
                // See defaultProps
                value: null
            },
            wrappedFormState: {}
        };

        this._setRef = this._setRef.bind(this);
        this._setButtonRef = this._setButtonRef.bind(this);
        this._setInputLineWidthRef = this._setInputLineWidthRef.bind(this);
        this._setColorPickerRef = this._setColorPickerRef.bind(this);
        this._setOverlayPanel = this._setOverlayPanel.bind(this);
        this._setOverlayPanelToolBox = this._setOverlayPanelToolBox.bind(this);
        this._clearCanvas = this._clearCanvas.bind(this);
        this._saveCanvas = this._saveCanvas.bind(this);
        this._loadCanvas = this._loadCanvas.bind(this);
        this._eraseCanvas = this._eraseCanvas.bind(this);
        this._drawCanvas = this._drawCanvas.bind(this);
        this._toggleCanvas = this._toggleCanvas.bind(this);
        this._toggleToolBox = this._toggleToolBox.bind(this);
        this._changeLineColor = this._changeLineColor.bind(this);
        this._changeLineWidth = this._changeLineWidth.bind(this);
        this._canvasOnBegin = this._canvasOnBegin.bind(this);
        this._canvasHide = this._canvasHide.bind(this);
        this._cancelCanvas = this._cancelCanvas.bind(this);
        this._onHideCanvas = this._onHideCanvas.bind(this);
    }

    shouldComponentUpdate(nextProps: Readonly<Props>, nextState: Readonly<State>, nextContext: any): boolean {
        return ObjectHelper.hasDifference(nextState, this.state);
    }

    componentDidUpdate(): void {
        if (this._button) {
            if (this.state.wrappedCompState.tabindex !== this.___tabindex) {
                ComponentHelper.tabindex(this.displayMode, this._button, this.state.wrappedCompState.tabindex);
                this.___tabindex = this.state.wrappedCompState.tabindex;
            }
        }
    }

    componentWillUnmount(): void {
        this._signature = null;
        this._inputLineWidth = null;
        this._colorPicker = null;
        this._overlayPanel = null;
        this._overlayPanelToolBox = null;
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
        this.emitDidCatch(error);
    }

    emitDidCatch(error: Error | any): void {
        Debug.console.error(error);
    }

    emitSubscriptionReject(): void {
    }

    private _callbackSetState() {
        const timeStamp = this.state.wrappedCompState.value ? new Date().toISOString() : null;
        this.emit(SignatureHelper.makeValue(this.state.wrappedCompState.value, this.state.wrappedCompState.metaInfo, timeStamp));
    }

    render(): React.ReactNode {
        const {
            show,
            displayMode
        } = this.state;

        if (!show) {
            return null;
        } else {
            switch (displayMode) {
                case ComponentElementDisplayMode.EDIT:
                    return this._render4Edit();
                case ComponentElementDisplayMode.READONLY:
                    return this._render4ReadOnly();
                case ComponentElementDisplayMode.DISABLED:
                    return this._render4Disabled();
                case ComponentElementDisplayMode.PRINT:
                    return this._render4Print();
                default:
                    return null;
            }
        }
    }

    private _render(newProps: WrappedCompState): React.ReactNode {
        const parentHeight = newProps.signStyle.height ? newProps.signStyle.height : this.parentDom.offsetHeight + "px";
        const margin = newProps.signStyle.margin ? newProps.signStyle.margin : "auto";
        const image = newProps.value ? <img src={newProps.value} style={{margin: margin, display: "block", opacity: "0"}} onLoad={SignatureHelper.adjustImageSize} /> : <div>&nbsp;</div>;
        if (newProps.readOnly) {
            return (
                <React.Fragment>
                    <div ref={this._setRef} className={"w-100 h-100 d-flex"}>
                        <div className="w-100 d-flex" style={{ height: parentHeight + "px" }}>
                            {image}
                        </div>
                    </div>
                </React.Fragment>
            );
        } else {
            const penColor = this._hexToRGB(newProps.userProps.lineColor);
            return (
                <React.Fragment>
                    <div className="w-100 h-100 d-flex">
                        <div className={"bf-cursor-pointer " + (!newProps.value ? "d-none" : "d-flex")}
                             style={{ width: newProps.showIcon ? "calc(100% - 30px)" : "100%", height: parentHeight + "px" }} onClick={this._toggleCanvas}>
                            {image}
                        </div>
                        <div className={"align-items-center justify-content-center bf-cursor-pointer " + (newProps.value ? "d-none" : "d-flex")}
                             style={{ width: newProps.showIcon ? "calc(100% - 30px)" : "100%", height: parentHeight + "px" }} onClick={this._toggleCanvas}>
                            <div className="w-100 bf-noBr text-center">{newProps.placeholder}</div>
                        </div>
                        <Button
                            icon="pi pi-pencil"
                            onClick={this._toggleCanvas}
                            className={"p-button-primary " + (!newProps.showIcon ? "d-none" : "")}
                            style={{height: parentHeight + "px", minHeight: "30px", verticalAlign: "top"}}
                            title="sign"
                            ref={this._setButtonRef}
                        />
                    </div>
                    <OverlayPanel
                        key={"canvasOverlay_" + this.elementId}
                        id={"canvasOverlay_" + this.elementId}
                        ref={this._setOverlayPanel}
                        showCloseIcon={false}
                        dismissable={true}
                        appendTo={DomHelper.getForOverLayDom()}
                        onHide={this._onHideCanvas}
                        style={{marginTop: "-2px", marginBottom: "-2px"}}
                    >
                        <div className={"w-100 border position-relative"}>
                            <SignatureCanvas
                                key={"SignatureCanvas_" + this.elementId}
                                minWidth={(newProps.userProps.lineWidth)}
                                penColor={"rgb(" + penColor.r + "," + penColor.g + "," + penColor.b + ")"}
                                canvasProps={{
                                    width: newProps.canvasStyle.width,
                                    height: newProps.canvasStyle.height,
                                    className: "sigCanvas"
                                }}
                                ref={this._setRef}
                                onBegin={this._canvasOnBegin}
                            />
                        </div>
                        <div className="w-100">
                            {this._renderAction()}
                        </div>
                    </OverlayPanel>
                </React.Fragment>
            );
        }
    }

    private _renderAction() {
        return (
            <div className="pt-1 pb-4 pr-0 border-0 mb-1" style={{backgroundColor: "transparent"}}>
                <div className="p-toolbar-group-left position-relative">
                    {this._renderCanvasActionsArea()}
                </div>
                <div className="p-toolbar-group-right">
                    <Button
                        key={"ButtonCancel_" + this.elementId}
                        icon={"pi pi-times"}
                        label={"Cancel"}
                        onClick={this._cancelCanvas}
                        className={"p-button-secondary mr-1"}
                    />
                    <Button
                        key={"ButtonSave_" + this.elementId}
                        icon={"pi pi-check"}
                        label={"Save"}
                        onClick={this._saveCanvas}
                        className={"p-button-success"}
                    />
                </div>
            </div>
        );
    }

    private _renderCanvasActionsArea() {
        if (parseInt(this.state.wrappedCompState.canvasStyle.width) < (this.state.wrappedCompState.canvasStyle.allowToModifyStyle ? 370 : 280) ) {
            return (
                <React.Fragment>
                    <Button
                        key={"ButtonToggleBox_" + this.elementId}
                        icon="fas fa-wrench"
                        onClick={this._toggleToolBox}
                        className={"p-button-secondary"}
                        style={{width: "1.9rem"}}
                    />
                    <div className="position-absolute d-none" style={{left: 5, bottom: 40, width: "230px", padding: "2px", backgroundColor: "#ffffff"}} ref={this._setOverlayPanelToolBox}>
                        {this._renderCanvasActions()}
                    </div>
                </React.Fragment>
            );
        } else {
            return (
                <React.Fragment>
                    {this._renderCanvasActions()}
                </React.Fragment>
            );
        }
    }

    private _renderCanvasActions() {
        return(
            <React.Fragment>
                <Button
                    key={"ButtonTrash_" + this.elementId}
                    icon="fas fa-trash"
                    onClick={this._clearCanvas}
                    className={"p-button-secondary"}
                    style={{width: "1.9rem"}}
                />
                <i className={"pi pi-bars p-toolbar-separator ml-1 mr-1"}/>
                <Button
                    key={"ButtonEraser_" + this.elementId}
                    icon="fas fa-eraser"
                    onClick={this._eraseCanvas}
                    className={" " + (this.state.wrappedCompState.isErase ? "p-button-primary" : "p-button-secondary")}
                    style={{width: "1.9rem"}}
                />
                <Button
                    key={"ButtonPencil_" + this.elementId}
                    icon="fas fa-pencil-alt"
                    onClick={this._drawCanvas}
                    className={" " + (this.state.wrappedCompState.isErase ? "p-button-secondary" : "p-button-primary")}
                    style={{width: "1.9rem"}}
                />
                <i className={"pi pi-ellipsis-v p-toolbar-separator ml-1 mr-1 " + (this.state.wrappedCompState.canvasStyle.allowToModifyStyle ? "d-inline-block" : "d-none")}/>
                <Spinner
                    key={"Spinner_" + this.elementId}
                    value={this.state.wrappedCompState.userProps.lineWidth}
                    onChange={this._changeLineWidth}
                    step={1}
                    min={1}
                    style={{width: "50px", marginBottom: "2px"}}
                    className={(this.state.wrappedCompState.canvasStyle.allowToModifyStyle ? "d-inline-block" : "d-none")}
                    ref={this._setInputLineWidthRef}
                />
                <div className={(this.state.wrappedCompState.canvasStyle.allowToModifyStyle ? "d-inline-block" : "d-none")}>
                    <ColorPicker
                        key={"ColorPicker_" + this.elementId}
                        value={this.state.wrappedCompState.userProps.lineColor}
                        onChange={this._changeLineColor}
                        ref={this._setColorPickerRef}
                    />
                </div>
            </React.Fragment>
        );
    }

    private _cancelCanvas() {
        this.setState({wrappedCompState: {...this.state.wrappedCompState, changed: false}}, this._canvasHide);
    }

    private _clearCanvas() {
        if (this._signature && this._signature.clear) {
            this._signature.clear();
            this.setState({wrappedCompState: {...this.state.wrappedCompState, changed: true}});
        }
    }

    private _saveCanvas() {
        if (this._signature && this._signature.toData) {
            if (this.state.wrappedCompState.changed) {
                const signatureData = this._signature.toData();
                this.setState({
                    wrappedCompState: {
                        ...this.state.wrappedCompState,
                        value: Array.isArray(signatureData) && signatureData.length > 0 ? this._signature.toDataURL() : null
                    }
                }, this._callbackSetState);
            }
            this._canvasHide();
        }
    }

    private _onHideCanvas() {
        if (this.state.wrappedCompState.autoSaveOnHide) {
            this._saveCanvas();
        }
    }

    private _canvasHide() {
        if (this._overlayPanel && this._overlayPanel.hide) {
            this._overlayPanel.hide();
        }
        if (this._overlayPanelToolBox) {
            this._overlayPanelToolBox.classList.add("d-none");
        }
    }

    private _hexToRGB(hex) {
        const m = hex.match(/^#?([\da-f]{2})([\da-f]{2})([\da-f]{2})$/i);
        return {
            r: parseInt(m[1], 16),
            g: parseInt(m[2], 16),
            b: parseInt(m[3], 16)
        };
    }

    private _loadCanvas() {
        if (this._signature && this._signature.clear) {
            this._signature.clear();
            this._signature.fromDataURL(
                this.state.wrappedCompState.value ? this.state.wrappedCompState.value : "data:,",
                {
                    ...this.state.wrappedCompState.canvasStyle,
                    className: "sigCanvas"
                }
            );
            this.setState({wrappedCompState: {...this.state.wrappedCompState, changed: false}});
        }
    }

    private _eraseCanvas() {
        if (this._signature && this._signature.getSignaturePad) {
            this._signature.getSignaturePad()._ctx.globalCompositeOperation = "destination-out";
            this.setState({wrappedCompState: {...this.state.wrappedCompState, isErase: true}});
        }
    }

    private _drawCanvas() {
        if (this._signature && this._signature.getSignaturePad) {
            this._signature.getSignaturePad()._ctx.globalCompositeOperation = "source-over";
            this.setState({wrappedCompState: {...this.state.wrappedCompState, isErase: false}});
        }
    }

    private _changeLineColor(e) {
        if (this._signature && this._signature.getSignaturePad) {
            const penColor = this._hexToRGB(e.value);
            this._signature.getSignaturePad().penColor = "rgb(" + penColor.r + "," + penColor.g + "," + penColor.b + ")";
            this.setState({
                wrappedCompState: {
                    ...this.state.wrappedCompState,
                    userProps: { ...this.state.wrappedCompState.userProps, lineColor: e.value }
                }
            });
        }
    }

    private _changeLineWidth(e) {
        if (this._signature && this._signature.getSignaturePad) {
            this._signature.getSignaturePad().minWidth = parseFloat(e.target["value"]);
            this.setState({
                wrappedCompState: {
                    ...this.state.wrappedCompState,
                    userProps: { ...this.state.wrappedCompState.userProps, lineWidth: e.target["value"] }
                }
            });
        }
    }

    private _canvasOnBegin() {
        this.setState({wrappedCompState: {...this.state.wrappedCompState, changed: true}});
        if (this._colorPicker) {
            this._colorPicker.hide();
        }
        if (this._overlayPanelToolBox) {
            this._overlayPanelToolBox.classList.add("d-none");
        }
    }

    private _toggleCanvas(e) {
        if (this._overlayPanel && this._overlayPanel.toggle) {
            this._overlayPanel.toggle(e);
            if (this._overlayPanel.isVisible()) {
                if (this._signature) {
                    this._signature.clear();
                    this._signature.getSignaturePad()._ctx.globalCompositeOperation = "source-over";
                    this._signature.fromDataURL(
                        this.state.wrappedCompState.value ? this.state.wrappedCompState.value : "data:,",
                        {
                            ...this.state.wrappedCompState.canvasStyle,
                            className: "sigCanvas"
                        }
                    );
                    this.setState({wrappedCompState: {...this.state.wrappedCompState, isErase: false, changed: false}});
                }
            }
        }
    }

    private _toggleToolBox(e) {
        if (this._overlayPanelToolBox) {
            if (this._overlayPanelToolBox.classList.contains("d-none")) {
                this._overlayPanelToolBox.classList.remove("d-none");
            } else {
                this._overlayPanelToolBox.classList.add("d-none");
            }
        }
    }

    private _render4Edit(): React.ReactNode {
        const newProps = {
            ...this.state.wrappedCompState,
            readOnly: false
        };

        return this._render(newProps);
    }

    private _render4ReadOnly(): React.ReactNode {
        const newProps = {
            ...this.state.wrappedCompState,
            readOnly: true
        };

        return this._render(newProps);
    }

    private _render4Disabled(): React.ReactNode {
        return this._render4ReadOnly();
    }

    private _render4Print(): React.ReactNode {
        return this._render4ReadOnly();
    }

    private _setRef(ref) {
        if (ref) {
            this._signature = ref;
            this.readyComponent();
        }
    }

    private _setButtonRef(ref) {
        if (ref) {
            this._button = ref.element;
        }
    }

    private _setInputLineWidthRef(ref) {
        if (ref) {
            this._inputLineWidth = ref;
        }
    }

    private _setColorPickerRef(ref) {
        if (ref) {
            this._colorPicker = ref;
        }
    }

    private _setOverlayPanel(ref) {
        if (ref) {
            this._overlayPanel = ref;
        }
    }

    private _setOverlayPanelToolBox(ref) {
        if (ref) {
            this._overlayPanelToolBox = ref;
        }
    }
}
